/**
 * API functions for AI Response Drafts
 */

import { auth } from '../firebase';
import type { AIDraft, AIDraftListResponse } from '@/types/aiDraft';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://sales-api-768596480524.us-central1.run.app';

async function getAuthHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

/**
 * List AI drafts, optionally filtered by status
 */
export async function listAIDrafts(status?: string): Promise<AIDraftListResponse> {
  const headers = await getAuthHeader();
  const url = new URL(`${API_BASE_URL}/api/ai-drafts`);
  if (status) url.searchParams.set('status', status);

  const response = await fetch(url.toString(), { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Failed to list AI drafts: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get a single AI draft with full conversation context
 */
export async function getAIDraft(id: string): Promise<AIDraft> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/ai-drafts/${id}`, { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Failed to get AI draft: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Approve a draft (optionally with edited content)
 */
export async function approveAIDraft(id: string, draftContent?: string): Promise<AIDraft> {
  const headers = await getAuthHeader();
  const body: Record<string, unknown> = {};
  if (draftContent !== undefined) {
    body.draft_content = draftContent;
  }

  const response = await fetch(`${API_BASE_URL}/api/ai-drafts/${id}/approve`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Failed to approve AI draft: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Reject a draft with a reason
 */
export async function rejectAIDraft(id: string, reason: string): Promise<AIDraft> {
  const headers = await getAuthHeader();

  const response = await fetch(`${API_BASE_URL}/api/ai-drafts/${id}/reject`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ reason }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Failed to reject AI draft: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Send an approved draft via the appropriate channel
 */
export async function sendAIDraft(id: string): Promise<AIDraft> {
  const headers = await getAuthHeader();

  const response = await fetch(`${API_BASE_URL}/api/ai-drafts/${id}/send`, {
    method: 'POST',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Failed to send AI draft: ${response.statusText}`);
  }

  return response.json();
}
